/**
 * Spotlight-style typed input bubble. Docks to the bird's position at
 * 60 fps so it appears to hang off the sprite. Mirrors the feel of the
 * native `CompanionTypedInputOverlay.swift` — auto-focus on show,
 * Enter submits, Escape dismisses, outside click dismisses.
 */

const BUBBLE_OFFSET_X_FROM_BIRD_CENTER = 26 + 12;
const POSITION_TICK_MS = 1000 / 60;

export interface TypedInputBubbleOptions {
  placeholder: string;
  onSubmit: (value: string) => void;
  onDismiss: () => void;
  getBirdCenter: () => { x: number; y: number };
}

export class TypedInputBubble {
  private readonly bubbleElement: HTMLDivElement;
  private readonly inputElement: HTMLInputElement;
  private readonly options: TypedInputBubbleOptions;
  private positionTimerId: number | null = null;
  private outsideClickListener: ((event: MouseEvent) => void) | null = null;

  constructor(options: TypedInputBubbleOptions) {
    this.options = options;
    this.bubbleElement = document.createElement("div");
    this.bubbleElement.className = "koko-pixel-box koko-input-bubble";

    const fillLayer = document.createElement("div");
    fillLayer.className = "koko-pixel-box-fill";

    this.inputElement = document.createElement("input");
    this.inputElement.type = "text";
    this.inputElement.placeholder = options.placeholder;
    this.inputElement.setAttribute("autocomplete", "off");
    this.inputElement.setAttribute("spellcheck", "false");

    const enterHint = document.createElement("span");
    enterHint.className = "koko-enter-hint";
    enterHint.textContent = "↵";

    fillLayer.appendChild(this.inputElement);
    fillLayer.appendChild(enterHint);
    this.bubbleElement.appendChild(fillLayer);

    this.inputElement.addEventListener("keydown", this.handleKeydown);
  }

  /** Append the bubble into the shadow root container. Hidden by default. */
  mount(container: HTMLElement): void {
    container.appendChild(this.bubbleElement);
  }

  show(): void {
    this.positionToBird();
    this.bubbleElement.classList.add("visible");
    this.inputElement.value = "";
    // Focus must happen after the transition starts so the caret appears immediately.
    requestAnimationFrame(() => {
      this.inputElement.focus();
    });
    this.startPositionTimer();
    this.installOutsideClickListener();
  }

  hide(): void {
    this.bubbleElement.classList.remove("visible");
    this.stopPositionTimer();
    this.removeOutsideClickListener();
  }

  isVisible(): boolean {
    return this.bubbleElement.classList.contains("visible");
  }

  destroy(): void {
    this.hide();
    this.inputElement.removeEventListener("keydown", this.handleKeydown);
    this.bubbleElement.remove();
  }

  private handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      const trimmed = this.inputElement.value.trim();
      if (trimmed.length === 0) {
        return;
      }
      this.options.onSubmit(trimmed);
    } else if (event.key === "Escape") {
      event.preventDefault();
      this.options.onDismiss();
    }
  };

  private startPositionTimer(): void {
    if (this.positionTimerId !== null) return;
    this.positionTimerId = window.setInterval(
      () => this.positionToBird(),
      POSITION_TICK_MS,
    );
  }

  private stopPositionTimer(): void {
    if (this.positionTimerId !== null) {
      window.clearInterval(this.positionTimerId);
      this.positionTimerId = null;
    }
  }

  private positionToBird(): void {
    const birdCenter = this.options.getBirdCenter();
    const bubbleWidth = this.bubbleElement.offsetWidth || 340;
    const bubbleHeight = this.bubbleElement.offsetHeight || 48;
    const viewportWidth = window.innerWidth;

    let leftEdge = birdCenter.x + BUBBLE_OFFSET_X_FROM_BIRD_CENTER;
    if (leftEdge + bubbleWidth > viewportWidth - 16) {
      // Flip to the left of the bird if we'd overflow the right edge.
      leftEdge = birdCenter.x - BUBBLE_OFFSET_X_FROM_BIRD_CENTER - bubbleWidth;
    }
    leftEdge = Math.max(8, leftEdge);

    const topEdge = birdCenter.y - bubbleHeight / 2;
    // Position via left/top so CSS `transform: scale(...)` can own the
    // show/hide bounce animation without being overwritten every tick.
    this.bubbleElement.style.left = `${leftEdge}px`;
    this.bubbleElement.style.top = `${topEdge}px`;
  }

  private installOutsideClickListener(): void {
    if (this.outsideClickListener) return;
    this.outsideClickListener = (event: MouseEvent) => {
      // The input bubble lives inside the shadow root, so event.target from the
      // document's perspective will be our <koko-companion> host — we can't
      // distinguish outside vs inside. Use composedPath to walk the real tree.
      const path = event.composedPath();
      if (!path.includes(this.bubbleElement)) {
        this.options.onDismiss();
      }
    };
    // Capture phase + timeout so the click that *opened* us doesn't immediately close us.
    window.setTimeout(() => {
      if (this.outsideClickListener) {
        document.addEventListener("mousedown", this.outsideClickListener, true);
      }
    }, 0);
  }

  private removeOutsideClickListener(): void {
    if (this.outsideClickListener) {
      document.removeEventListener("mousedown", this.outsideClickListener, true);
      this.outsideClickListener = null;
    }
  }
}
